import { useEffect, useMemo, useState } from 'react';
import {
  reportsApi,
  type ObservationDispositionQueueResponse,
  type SafetyCaseRecord,
} from '../../services/reportsApi';
import { buildScanVerifyPath } from '../acceptance/scanVerifyNavigation';
import { collectLogicalScanSources, groupLogicalScans } from './firstEligibleLogicalScan';
import { presentFirstScanFinding, type FirstScanFindingView } from './firstScanFindingPresenter';

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? value as Record<string, unknown> : {};
}

export function useFirstScanFinding(args: {
  item: SafetyCaseRecord | null;
  workflowSummary?: Record<string, unknown> | null;
  workflowId: string;
  orgId?: string | null;
  siteId?: string | null;
  draftWindow?: { start?: string | null; end?: string | null } | null;
  locationLabel?: string | null;
  cameraId?: string | null;
  riskType?: string | null;
  timeZone?: string | null;
  baselineWaiting?: boolean;
  scanProgress?: ObservationDispositionQueueResponse['progress'] | null;
}): { view: FirstScanFindingView; count: number } {
  const [firstScanReport, setFirstScanReport] = useState<unknown>(null);
  const [firstScanProgress, setFirstScanProgress] = useState<ObservationDispositionQueueResponse['progress'] | null>(null);
  const logicalScanState = useMemo(() => {
    const item = args.item;
    if (!item) return { scan: null, count: 0 };
    const record = item as Record<string, unknown>;
    const summary = asRecord(args.workflowSummary);
    const history = Array.isArray(record.scan_history)
      ? record.scan_history as Array<Record<string, unknown>>
      : (Array.isArray(summary.scan_history) ? summary.scan_history as Array<Record<string, unknown>> : []);
    const firstIdentity = asRecord(record.first_eligible_scan).report_id
      ? asRecord(record.first_eligible_scan)
      : (asRecord(summary.first_eligible_scan).report_id ? asRecord(summary.first_eligible_scan) : null);
    const latestSigned = Object.keys(asRecord(record.latest_signed_scan)).length
      ? asRecord(record.latest_signed_scan)
      : asRecord(summary.latest_signed_scan);
    const sources = collectLogicalScanSources({
      scanHistory: history,
      activeDraftReportId: item.active_draft_report_id,
      latestFinalizedReportId: item.latest_finalized_report_id,
      firstEligibleScan: firstIdentity,
      orgId: item.org_id,
      siteId: item.site_id,
      workflowId: args.workflowId,
      draftWindow: args.draftWindow,
      signedWindow: {
        start: String(latestSigned.scope_start || latestSigned.window_start_utc || '').trim() || null,
        end: String(latestSigned.scope_end || latestSigned.window_end_utc || '').trim() || null,
      },
    });
    const grouped = groupLogicalScans(sources, {
      orgId: item.org_id,
      siteId: item.site_id,
      workflowId: args.workflowId,
    });
    return { scan: grouped[0] || null, count: grouped.length };
  }, [
    args.draftWindow,
    args.item,
    args.workflowId,
    args.workflowSummary,
  ]);
  const scan = logicalScanState.scan;

  useEffect(() => {
    const reportId = String(scan?.reportId || '').trim();
    if (!reportId) {
      setFirstScanReport(null);
      setFirstScanProgress(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const [reportOpen, queue] = await Promise.all([
          reportsApi.openSafetyReport(reportId).catch(() => null),
          scan?.status === 'draft'
            ? reportsApi.getObservationDispositionQueue(reportId, { limit: 1 }).catch(() => null)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setFirstScanReport(reportOpen);
        setFirstScanProgress(queue?.progress || null);
      } catch {
        if (!cancelled) {
          setFirstScanReport(null);
          setFirstScanProgress(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scan?.reportId, scan?.status]);

  const view = useMemo(() => {
    const item = args.item;
    const openScanHref = scan && item
      ? buildScanVerifyPath({
        reportId: scan.reportId,
        orgId: args.orgId,
        siteId: args.siteId || item.site_id,
        safetyCaseId: String(item.safety_case_id || item.finding_id || '').trim() || null,
      })
      : '';
    const draftProgress = scan?.reportId && scan.reportId === String(item?.active_draft_report_id || '').trim()
      ? args.scanProgress
      : firstScanProgress;
    return presentFirstScanFinding({
      hasEligibleScan: Boolean(scan),
      baselineWaiting: args.baselineWaiting,
      reportId: scan?.reportId,
      signed: scan?.status === 'finalized',
      windowStart: scan?.windowStartUtc,
      windowEnd: scan?.windowEndUtc,
      timeZone: args.timeZone,
      report: firstScanReport,
      caseScope: asRecord(item?.scope),
      locationLabel: args.locationLabel,
      cameraId: args.cameraId,
      progress: (draftProgress || null) as Record<string, unknown> | null,
      riskType: args.riskType,
      logicalScanCount: logicalScanState.count || (scan ? 1 : 0),
      persistedRecurrence: null,
      openScanHref,
    });
  }, [
    args.baselineWaiting,
    args.cameraId,
    args.item,
    args.locationLabel,
    args.orgId,
    args.riskType,
    args.scanProgress,
    args.siteId,
    args.timeZone,
    firstScanProgress,
    firstScanReport,
    logicalScanState.count,
    scan,
  ]);

  return { view, count: logicalScanState.count };
}

export function useCaseHomeFirstScanFinding(page: {
  item: SafetyCaseRecord | null;
  workflowSummary?: Record<string, unknown> | null;
  workflowId: string;
  searchParams: URLSearchParams;
  scanQueueWindow?: { start?: string | null; end?: string | null } | null;
  scanReportAnchorWindow?: { start?: string | null; end?: string | null } | null;
  scanLocationLabel?: string | null;
  scanCameraId?: string | null;
  siteTimeZone?: string | null;
  operatorLifecycle?: { operational_state?: string | null } | null;
  scanProgress?: ObservationDispositionQueueResponse['progress'] | null;
  riskType?: string | null;
}): { view: FirstScanFindingView; count: number } {
  return useFirstScanFinding({
    item: page.item,
    workflowSummary: page.workflowSummary,
    workflowId: page.workflowId,
    orgId: page.searchParams.get('org_id'),
    siteId: page.item?.site_id || page.searchParams.get('site_id'),
    draftWindow: {
      start: page.scanQueueWindow?.start
        || page.scanReportAnchorWindow?.start
        || String(page.item?.time_window?.start || '').trim()
        || null,
      end: page.scanQueueWindow?.end
        || page.scanReportAnchorWindow?.end
        || String(page.item?.time_window?.end || '').trim()
        || null,
    },
    locationLabel: page.scanLocationLabel,
    cameraId: page.scanCameraId
      || String(page.item?.camera_id || page.item?.scope?.camera_id || '').trim()
      || null,
    riskType: page.riskType,
    timeZone: page.siteTimeZone,
    baselineWaiting: String(page.operatorLifecycle?.operational_state || '').trim() === 'baseline_waiting',
    scanProgress: page.scanProgress,
  });
}
